import { useState } from 'react';

import { matchingSession } from '../session';

// Reads the module singleton into React state on mount; every setter writes through to the
// singleton first so it stays the source of truth across navigation (session.ts).
export const useMatchingSession = () => {
  const [selectedIds, setSelectedIds] = useState<number[]>(matchingSession.selectedQuestionnaireIds);
  const [sentPairKeys, setSentPairKeys] = useState<string[]>(matchingSession.sentPairKeys);

  const persist = (ids: number[]) => {
    matchingSession.selectedQuestionnaireIds = ids;
    setSelectedIds(ids);
  };

  const toggleId = (id: number) => {
    persist(selectedIds.includes(id) ? selectedIds.filter(existing => existing !== id) : [...selectedIds, id]);
  };

  // Adds the group's visible ids, top-to-bottom, until the cap — never past it.
  const selectGroup = (visibleIds: number[], max: number) => {
    const room = Math.max(max - selectedIds.length, 0);
    const additions = visibleIds.filter(id => !selectedIds.includes(id)).slice(0, room);

    if (additions.length > 0) {
      persist([...selectedIds, ...additions]);
    }
  };

  const deselectGroup = (groupIds: number[]) => {
    persist(selectedIds.filter(id => !groupIds.includes(id)));
  };

  // Reads the singleton, not the `selectedIds` closure — called from an effect that may hold a stale one.
  const dropIds = (ids: number[]) => {
    persist(matchingSession.selectedQuestionnaireIds.filter(id => !ids.includes(id)));
  };

  const clearSelection = () => {
    persist([]);
  };

  const setRunId = (runId: string | null) => {
    matchingSession.runId = runId;
  };

  const markPairSent = (pairKey: string) => {
    const keys = [...matchingSession.sentPairKeys, pairKey];
    matchingSession.sentPairKeys = keys;
    setSentPairKeys(keys);
  };

  return {
    selectedIds,
    toggleId,
    selectGroup,
    deselectGroup,
    clearSelection,
    dropIds,
    setRunId,
    sentPairKeys,
    markPairSent,
  };
};
